import { Box, Typography } from '@mui/joy';

// Логотипы банков (инлайн-SVG по коду банка). Нет в карте → показываем короткую
// подпись `short` (см. BANKS ниже). Это упрощённые бренд-марки — при желании
// вставьте официальные SVG банков прямо сюда (тот же ключ = код банка).
const BANK_LOGOS = {
    sber: (
        <svg viewBox="0 0 24 24" width="64%" height="64%" fill="none" aria-hidden="true">
            <path d="M20.5 7A9 9 0 1 0 21 12" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" />
            <path d="M7.4 11.2l3.7 3.4 6.2-7.2" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    tinkoff: (
        <svg viewBox="0 0 24 24" width="70%" height="70%" aria-hidden="true">
            <path d="M12 2.4l8 2.4v6.5c0 4.7-3.2 8-8 10-4.8-2-8-5.3-8-10V4.8l8-2.4z" fill="#111" />
            <path d="M8.2 9h7.6M12 9v7" stroke="#FFDD2D" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
    ),
    vtb: (
        <svg viewBox="0 0 24 24" width="72%" height="72%" aria-hidden="true">
            <path d="M3.4 7.5h5.3l3.3 6.1 3.3-6.1h5.3L14 17.6h-4L3.4 7.5z" fill="#fff" />
        </svg>
    ),
    ozon: (
        <svg viewBox="0 0 24 24" width="58%" height="58%" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="6.4" stroke="#fff" strokeWidth="2.6" />
        </svg>
    ),
};

// Брендинг банков: цвет + короткая подпись `short` (fallback, если нет логотипа).
export const BANKS = {
    alpha:      { name: 'Альфа Банк',   short: 'А',   color: '#EF3124', textColor: '#fff' },
    sber:       { name: 'Сбер',         short: 'Сб',  color: '#21A038', textColor: '#fff' },
    tinkoff:    { name: 'Т-Банк',       short: 'Т',   color: '#FFDD2D', textColor: '#000' },
    vtb:        { name: 'ВТБ',          short: 'ВТ',  color: '#0A2973', textColor: '#fff' },
    gazprom:    { name: 'Газпромбанк',  short: 'ГП',  color: '#0079C2', textColor: '#fff' },
    raiffeisen: { name: 'Райффайзен',   short: 'Ра',  color: '#FEDF00', textColor: '#000' },
    opening:    { name: 'Открытие',     short: 'От',  color: '#00BFFF', textColor: '#fff' },
    sovcombank: { name: 'Совкомбанк',   short: 'Со',  color: '#D52127', textColor: '#fff' },
    ozon:       { name: 'Озон Банк',    short: 'Oz',  color: '#005BFF', textColor: '#fff' },
    akbars:     { name: 'Ак Барс Банк', short: 'Ак',  color: '#00A859', textColor: '#fff' },
    other:      { name: 'Банк',         short: '₽',   color: '#6B7280', textColor: '#fff' },
};

/**
 * Эмблема банка: цветной кружок с логотипом (инлайн-SVG из BANK_LOGOS).
 * Нет логотипа → короткая подпись `short` (напр. «Ак» для Ак Барса и «А» для
 * Альфы — уже не путаются); нет и её → первая буква названия.
 */
export function BankBadge({ bank, size = 22, logoUrl = null, color = null }) {
    const meta = BANKS[bank] || BANKS.other;
    const logo = BANK_LOGOS[bank];
    const label = meta.short || meta.name.charAt(0);
    const bg = color || meta.color;
    return (
        <Box sx={{
            width: size, height: size,
            borderRadius: '50%',
            bgcolor: bg,
            color: meta.textColor,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800,
            // 2-символьная подпись — мельче, чтобы влезла в кружок
            fontSize: size * (label.length > 1 ? 0.4 : 0.52),
            lineHeight: 1,
            flexShrink: 0,
            overflow: 'hidden',
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
        }}>
            {logoUrl
                ? <img src={logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                : (logo || label)}
        </Box>
    );
}

/**
 * Форматирует "79990777777" → "+7 (999) 077-77-77"
 */
export function formatPhone(phone) {
    if (!phone) return '';
    const digits = String(phone).replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('7')) {
        return `+7 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`;
    }
    return phone;
}

/**
 * Форматирование с пробелами между каждой цифрой (для big-display карточки):
 * "79990777777" → "+7 9 9 9 0 7 7 7 7 7 7"
 */
export function formatPhoneSpaced(phone) {
    if (!phone) return '';
    let digits = String(phone).replace(/\D/g, '');

    // 8XXXXXXXXXX / 7XXXXXXXXXX → код страны + 10 цифр
    let prefix = '';
    if (digits.length === 11 && (digits[0] === '7' || digits[0] === '8')) {
        prefix = '+7 ';
        digits = digits.slice(1);
    }

    // 10 цифр → «995 287 12 51» (группы 3-3-2-2)
    if (digits.length === 10) {
        return `${prefix}${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
    }

    // нестандартная длина — отдаём как есть (с префиксом, если был)
    return prefix + digits;
}


/**
 * Итоговый бренд банка: серверные логотип/цвет в приоритете, иначе — из BANKS.
 * data = { logoUrl, color } (обычно из полей ответа сервера).
 */
export function resolveBankBrand(bank, { logoUrl = null, color = null } = {}) {
    const meta = BANKS[bank] || BANKS.other;
    return { ...meta, color: color || meta.color, logoUrl: logoUrl || null };
}